import { Fragment, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown, ChevronLeft, ChevronRight, Columns3, LayoutGrid, LayoutTemplate, Link2, PanelLeft, PanelRight, Plus } from 'lucide-react'
import { collectBlockTypes } from './normalizeRegistry'
import { normalizePage } from './normalizePage'
import { useBuilder } from './hooks/useBuilder'
import { DEVICE_WIDTHS } from './BuilderContext'
import { RBOX_CLASS, HIDE_ATTR, hideAttrValue } from './editable/responsiveVisibility'
import { BuilderBlockWrapper } from './BuilderBlockWrapper'
import { BuilderBlockCatalog, DASHBOARD_CATEGORY, SYSTEM_CATEGORY } from './BuilderBlockCatalog'
import { DASHBOARD_GRID_TYPE, withDashboardGrid } from './dashboardGrid'
import { BuilderExistingBlockPicker, SHARED_CARRIER_TYPE } from './BuilderExistingBlockPicker'
import { BuilderGridLayout } from './BuilderGridLayout'
import { BuilderBlockMount } from './BuilderBlockMount'
import { loadDashboard, DashboardAccessError } from './hostAdapters'
import { FeatureUnavailable } from './FeatureUnavailable'
import { notify } from '../../notices'
import { BuilderChatPanel, REGISTRY_INVALIDATE_EVENT, REGISTRY_REFRESH_EVENT } from './chat/BuilderChatPanel'
import { consumePlacementFor, peekPendingPlacements, reservedSpotsFor, resolvePlacements } from './blockPlacement'
import { BuilderBlockSkeleton } from './BuilderBlockSkeleton'
import { VersionCompareSummary, VersionDiffFrame } from './BuilderVersions'
import { useChatTurns, liveConversationIds } from './chat/turnStatus'
import { useEscapeClose } from '../shared/hooks/useEscapeClose'
import { largeurUtile } from '../shell/cornerDock';

// A dashboard opens with a single grid block; widgets are composed into its
// cells — cf. dashboardGrid.js, qui porte la règle et le pourquoi.

// Les GABARITS EN COLONNES offerts au menu « Ajouter », sous « Mise en page ».
// Comme la grille, ce sont des conteneurs vides : le catalogue n'aurait rien à
// en montrer (leur catégorie est « system »), et c'est ce menu qui les rend
// atteignables. Déf absente du registre (jamais importée) → pas d'entrée,
// plutôt qu'un clic sans effet. Les trois presets ne diffèrent que par la
// liste `columns:` de leur YAML — un quatrième gabarit s'ajoute ici et dans
// config/builder/blocks/, sans une ligne de JSX.
const LAYOUT_PRESETS = [
  { type: 'LayoutColumnLeft', Icon: PanelLeft, label: 'Colonne à gauche', hint: 'Une colonne latérale à gauche, le contenu à droite' },
  { type: 'LayoutColumnRight', Icon: PanelRight, label: 'Colonne à droite', hint: 'Le contenu à gauche, une colonne latérale à droite' },
  { type: 'LayoutColumnBoth', Icon: Columns3, label: 'Colonnes à gauche et à droite', hint: 'Le contenu au centre, une colonne latérale de chaque côté' },
]

// Les défs que tout canevas peut avoir à poser sans passer par le catalogue :
// le porteur d'un bloc lié, les gabarits en colonnes du menu « Ajouter », la
// grille d'un tableau de bord. Chargées avec celles de la page.
const BASE_TYPES = [SHARED_CARRIER_TYPE, DASHBOARD_GRID_TYPE, ...LAYOUT_PRESETS.map(p => p.type)]

/**
 * Props :
 *  - slug          — page slug to load via /api/builder/page/{slug}
 *  - objectContext — optional `_context_` payload (resolved object data,
 *                    already mapped by the caller). Injected into each
 *                    bloc's dataOverride under the reserved key
 *                    `_context_` so block YAMLs can read `~~._context_.X`.
 *  - apiPrefix     — optional URL prefix under which the page must be
 *                    fetched (`/en` → Drupal negotiates that language and
 *                    serves the page's translation). Empty for the default
 *                    language. Handed over by `/api/builder/resolve`.
 *  - alternates    — `{langcode: url}` of the page in each of its languages
 *                    (from resolve), exposed to blocks as `~~._page_.alternates`
 *                    so a language switcher leads to THIS page translated.
 */
export const BuilderCanvas = ({ slug, objectContext, apiPrefix = '', alternates }) => {
  const { page, setPage, registry, loadRegistryTypes, refreshRegistry, isEditMode, previewDevice, layoutMode, host, isDirty, contentDirty, ensureBlockAtEnd, addBlock, editScope, versionView, reloadToken } = useBuilder()
  // Composer la PAGE (ajouter, déplacer, masquer un bloc, appeler l'assistant)
  // n'a lieu d'être que dans une session d'édition de page. Ouvert sur un
  // OBJET, le même canevas ne sert que de lunette : il rend le modèle comme le
  // visiteur le verra — `display: contents` et visibilité responsive comprises,
  // sinon un en-tête `sticky` ne collerait pas et un bloc masqué s'afficherait.
  const canEditStructure = isEditMode && editScope !== 'object'
  const [loading, setLoading] = useState(true)
  // Résilience host dashboard : chargement en échec (module orbit_dashboard off,
  // ou tableau introuvable) → on affiche un message au lieu d'un canvas cassé.
  const [hostUnavailable, setHostUnavailable] = useState(false)
  const [accessDenied, setAccessDenied] = useState(false)
  const [catalogPosition, setCatalogPosition] = useState(null)
  // Deux gestes distincts au « + » : poser un bloc NEUF depuis le catalogue
  // (son contenu est une copie de la graine), ou réutiliser un bloc EXISTANT
  // par référence (son contenu reste commun à toutes ses places).
  const [catalogMode, setCatalogMode] = useState('new')

  // Relire LES SEULES défs nommées (fin d'un tour de l'assistant : il a touché
  // un bloc, pas deux cents), ou — repli d'un tour qui ne sait pas nommer ce
  // qu'il a touché — tout ce que le registre connaît + le catalogue du site.
  // Chargeurs et règles : BuilderContext.
  const reloadRegistryTypes = useCallback(
    (types) => loadRegistryTypes(types, { force: true }),
    [loadRegistryTypes],
  )
  const reloadRegistry = refreshRegistry

  // Un tour de l'assistant est-il en cours, et l'éditeur avait-il cliqué « + »
  // à un endroit de CETTE page avant de le lancer ? Alors la place est réservée
  // à l'écran pendant toute l'attente. On LIT le placement sans le consommer :
  // il reste dû à la fin du tour, seule autorisée à le dépenser.
  //
  // C'est le PLACEMENT qui décide, pas l'intention : « créer », « créer à
  // partir de » et « modifier ce bloc puis le poser » viennent toutes du même
  // geste — un clic à un endroit précis — et méritent toutes le même repère.
  const chatTurns = useChatTurns()
  // Une place réservée PAR DEMANDE, et plus une seule : l'éditeur peut cliquer
  // « + » sous un bloc en train d'être fabriqué et en décrire un second sans
  // attendre. Chaque carré tient tant que SON tour tourne — le tour est nommé
  // par le placement (cf. blockPlacement), donc un tour d'un autre onglet ou
  // d'une autre page n'en rallume aucun.
  const skeletonSlots = useMemo(() => {
    if (!isEditMode) return []
    const live = liveConversationIds(chatTurns)
    const total = page?.blocs?.length ?? 0
    return reservedSpotsFor(peekPendingPlacements(), live, spot => spot?.kind === 'page')
      .map(spot => Number(spot.at))
      .filter(at => Number.isFinite(at))
      .map(at => Math.min(Math.max(0, at), total))
  }, [chatTurns, isEditMode, page?.blocs?.length])

  /** Combien de carrés d'attente se rangent devant le bloc d'indice `idx`. */
  const skeletonsAt = useCallback(
    (idx) => skeletonSlots.filter(at => at === idx).length,
    [skeletonSlots],
  )

  // Which defs this canvas offers at TOP LEVEL. A dashboard host composes like
  // a page (generic blocks + dashboard grids) but not the individual widgets —
  // those live inside a grid cell. Shared by the "+" catalogue and by the
  // assistant's auto-add, so both agree on what belongs here.
  const catalogAccept = useCallback((def) => (
    def.category !== SYSTEM_CATEGORY && (
      host
        ? def.type === DASHBOARD_GRID_TYPE || def.category !== DASHBOARD_CATEGORY
        : def.category !== DASHBOARD_CATEGORY
    )
  ), [host])

  // The assistant finished a turn that created/updated block defs: reload the
  // defs it TOUCHED — those blocks remount on the canvas (their `version`
  // changes) and the catalogue popin lists a new one —, then put a block the
  // page doesn't have yet ON THE CANVAS — the editor asked for it, it has to be
  // visible without hunting for it in the catalogue.
  //
  // WHERE it lands depends on how the turn started. Asked from the catalogue
  // ("Créer un bloc", "Créer à partir de celui-ci"), a placer was armed at the
  // spot the editor clicked "+" — page position or dashboard grid cell — and it
  // wins. Asked from the chat with no such gesture, there is no spot to honour:
  // the block goes at the end, as before.
  const handleRegistryInvalidate = useCallback(async (event) => {
    const types = Array.isArray(event?.detail?.types) ? event.detail.types : []
    // `signature` = the stored definition + the catalogue name. Comparing it
    // across the reload is how we tell a def the turn actually CHANGED from one
    // the agent merely talked about — `reloadRegistry` uses the same yardstick to
    // decide which instances remount.
    const before = new Map(registry.map(e => [e.type, e.signature]))
    // One-shot, and consumed even on a fruitless turn: a placer left armed would
    // drop a block somewhere the editor never asked for, several turns later.
    // La conversation dit LAQUELLE des demandes en attente ce tour-ci vient
    // remplir : plusieurs peuvent courir de front, et dépenser celle du voisin
    // poserait le bloc au mauvais endroit.
    const placement = consumePlacementFor(event?.detail?.conversationId ?? null)
    // Le tour NOMME ce qu'il a touché (le bloc épinglé, celui qu'un mutateur a
    // écrit) → on ne relit que ces défs-là. Sans nom — un mutateur dont le
    // `tool_result` n'a pas pu être lu —, on ne sait pas quoi relire : le
    // catalogue entier est le seul repli honnête.
    const wanted = [...new Set([...types, placement?.type].filter(Boolean))]
    const fresh = wanted.length ? await reloadRegistryTypes(wanted) : await reloadRegistry()
    // Un tour SANS types nommés a tout de même pu créer quelque chose : c'est le
    // cas de celui dont on a lâché le flux pour en lancer un autre, et dont la
    // fin est apprise par le sondage. S'il avait une place réservée, elle est
    // due — `resolvePlacements` sait la servir par différence de catalogue.
    if (!fresh || (types.length === 0 && !placement)) return

    const added = []
    if (placement) {
      // The editor clicked "+" at a spot before asking — `resolvePlacements` holds
      // the rule for what lands there. A placer knows its own target (a grid cell
      // takes widgets the page would refuse), so it is not second-guessed by the
      // top-level predicate.
      for (const def of resolvePlacements({ types, placement, before, fresh })) {
        placement.place(def)
        added.push(def.name || def.type)
      }
    }
    else if (!event?.detail?.background) {
      // Pas de place réservée : le bloc va en bas de page. Juste pour le tour
      // qu'on REGARDAIT — l'éditeur vient de le demander depuis cet écran.
      // Un tour appris par sondage, lui, peut venir d'un autre onglet ou d'une
      // page qu'on a quittée : y déposer un bloc serait une surprise, pas un
      // service (cf. BuilderChatPanel, l'annonce `background`).
      for (const type of types) {
        const def = fresh.find(e => e.type === type)
        // Unknown, or not placeable at this canvas's top level (a dashboard
        // widget, an object def…) → the catalogue refresh is all it gets.
        if (!def || !catalogAccept(def)) continue
        if (ensureBlockAtEnd(def)) added.push(def.name || def.type)
      }
    }

    if (added.length) {
      notify({
        type: 'success',
        message: placement
          ? `Ajouté : ${added.join(', ')}. Sauvegardez pour conserver.`
          : `Ajouté en bas de page : ${added.join(', ')}. Sauvegardez pour conserver.`,
      })
    }
  }, [reloadRegistry, reloadRegistryTypes, catalogAccept, ensureBlockAtEnd, registry])

  // Re-fetch a dashboard host's blocs without the full-screen loader. `fresh`
  // bypasses the browser cache, since the chat agent's write lands server-side
  // (sidecar) and can't invalidate this tab's cached collection GET.
  const reloadDashboard = useCallback(async () => {
    if (!host) return false
    try {
      const { title, blocs, hostMeta } = await loadDashboard(host, { fresh: true })
      setPage(prev => ({ ...prev, title, blocs: withDashboardGrid(blocs), hostMeta }))
      return true
    } catch (err) {
      console.error('Dashboard reload error:', err)
      notify({ type: 'error', message: 'Échec du rechargement du tableau de bord.' })
      return false
    }
  }, [host, setPage])

  // The chat agent wrote this dashboard's layout: auto-refresh to show it, unless
  // the user has unsaved local edits (structure or unit content). The two writes
  // are last-write-wins with no merge, so pending work is never clobbered — the
  // situation is surfaced and the user chooses.
  const handleDashboardInvalidate = useCallback(async () => {
    if (isDirty || contentDirty) {
      notify({ type: 'info', message: "L'assistant a modifié ce tableau de bord côté serveur. Vos changements locaux non sauvegardés ont la priorité : sauvegardez pour les garder (l'écriture de l'assistant sera écrasée), ou rechargez la page pour voir la version de l'assistant (vos changements locaux seront perdus)." })
      return
    }
    if (await reloadDashboard()) {
      notify({ type: 'success', message: "Tableau de bord mis à jour par l'assistant." })
    }
  }, [isDirty, contentDirty, reloadDashboard])

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      setHostUnavailable(false)
      setAccessDenied(false)
      // Les défs de BASE partent tout de suite ; celles des blocs de la page,
      // dès qu'on la connaît (`collectBlockTypes` lit aussi l'unité qu'un bloc
      // lié embarque). Le catalogue du site, lui, attend l'ouverture du « + ».
      // `loadRegistryTypes` ne lève jamais (null en échec) ; tout est attendu
      // dans le `finally`, donc le canevas ne s'affiche qu'avec ses défs.
      const registryLoads = [loadRegistryTypes(BASE_TYPES)]
      try {
        // A dashboard host loads its blocs from one `field_layout` JSON blob via
        // the decoupled API. Blocs carry no syncUrls → editable through page-state,
        // persisted by the dashboard's single save (useBuilderSave). An empty
        // dashboard opens with one grid block so the user composes into a grid.
        if (host) {
          const { title, blocs, hostMeta } = await loadDashboard(host)
          registryLoads.push(loadRegistryTypes(collectBlockTypes(blocs)))
          setPage(prev => ({ ...prev, slug: slug || '', title, blocs: withDashboardGrid(blocs), hostMeta, pageNid: null }))
          return
        }

        // A page host is slug-resolved on the backend and returns decoupled JSON.
        const apiRes = await fetch(`${apiPrefix}/api/builder/page/${slug}`, { credentials: 'include' })
        if (apiRes.ok) {
          const payload = await apiRes.json()
          registryLoads.push(loadRegistryTypes(collectBlockTypes(payload)))
          setPage(normalizePage(payload))
        } else if (apiRes.status === 404) {
          // Page pas encore créée : elle naîtra publiée (défaut Drupal), donc
          // la case du header part cochée.
          setPage(prev => ({ ...prev, slug: slug || '', title: '', published: true, blocs: [], pageNid: null }))
        } else {
          console.error('Builder: page fetch failed', apiRes.status)
        }
      } catch (err) {
        console.error('BuilderCanvas load error:', err)
        if (err instanceof DashboardAccessError) {
          setAccessDenied(true)
        } else if (host) {
          setHostUnavailable(true)
        } else {
          notify({ type: 'error', message: 'Échec du chargement de la page.' })
        }
      } finally {
        await Promise.all(registryLoads)
        setLoading(false)
      }
    }
    if (host || slug) load()
  // `reloadToken` : rechargement demandé (après une restauration de version,
  // les blocs ont changé en base).
  }, [slug, host, apiPrefix, setPage, loadRegistryTypes, reloadToken])

  // Un bloc dont la déf n'est pas encore là — posé depuis la bibliothèque des
  // blocs existants, revenu avec une version restaurée, affiché par une
  // comparaison de versions : on la charge. Les types déjà demandés ne
  // repartent pas (cf. loadRegistryTypes). La clé ne lit que le type des blocs
  // et des cellules de gabarit — pas leur contenu, qui change à chaque frappe :
  // l'effet ne repart que si la LISTE des types change. L'unité d'un bloc lié
  // ajouté en cours de session est chargée par UnitMount à son montage.
  const blocTypesKey = useMemo(() => {
    const types = new Set()
    const walk = (blocs) => (Array.isArray(blocs) ? blocs : []).forEach((b) => {
      if (b?.type) types.add(b.type)
      walk(b?.data?.cells)
    })
    walk(page?.blocs)
    return [...types].sort().join(',')
  }, [page?.blocs])
  useEffect(() => {
    loadRegistryTypes(blocTypesKey ? blocTypesKey.split(',') : [])
  }, [blocTypesKey, loadRegistryTypes])
  useEffect(() => {
    if (versionView) loadRegistryTypes(collectBlockTypes(versionView))
  }, [versionView, loadRegistryTypes])

  // Refresh the catalogue without a page reload when the chat agent
  // creates / updates a block_def.
  useEffect(() => {
    window.addEventListener(REGISTRY_INVALIDATE_EVENT, handleRegistryInvalidate)
    return () => window.removeEventListener(REGISTRY_INVALIDATE_EVENT, handleRegistryInvalidate)
  }, [handleRegistryInvalidate])

  // Un tour d'ARRIÈRE-PLAN vient de finir : le panneau ne sait pas ce qu'il a
  // touché (la liste des tours ne le dit pas), et personne ne le regardait. On
  // relit donc le registre, et RIEN d'autre — surtout pas
  // `handleRegistryInvalidate`, qui consomme au passage le placement armé par le
  // « + » de l'éditeur (il appartient peut-être à un tour encore en cours) et
  // peut poser un bloc en bas d'une page que personne ne regarde.
  useEffect(() => {
    const onRefresh = () => { reloadRegistry() }
    window.addEventListener(REGISTRY_REFRESH_EVENT, onRefresh)
    return () => window.removeEventListener(REGISTRY_REFRESH_EVENT, onRefresh)
  }, [reloadRegistry])

  // Index registry by type for O(1) lookup at render time.
  const registryByType = useMemo(() => {
    const map = {}
    for (const entry of registry) {
      if (entry.type) map[entry.type] = entry
    }
    return map
  }, [registry])

  const handleOpenCatalog = (position) => {
    setCatalogMode('new')
    setCatalogPosition(position)
  }

  const handleOpenExisting = (position) => {
    setCatalogMode('existing')
    setCatalogPosition(position)
  }

  const handleCloseCatalog = () => {
    setCatalogPosition(null)
  }

  // Grille et gabarits en colonnes : les seuls blocs qu'on pose SANS passer par
  // le catalogue. Ce sont des CONTENANTS vides — le catalogue n'aurait rien à
  // en montrer, et le contenu se choisit ensuite case par case (grille) ou
  // colonne par colonne (gabarit), dans le conteneur lui-même. Le geste est
  // donc direct : une entrée du menu les pose à la position visée.
  //
  // Leurs défs vivent en bibliothèque partagée, dans une catégorie que le
  // catalogue d'une page écarte (« Dashboard » pour la grille, « system » pour
  // les gabarits, cf. `catalogAccept`) : c'est ce menu qui les rend
  // atteignables. Déf absente du registre (jamais importée) → pas d'entrée,
  // plutôt qu'un clic qui ne fait rien.
  const gridDef = registryByType[DASHBOARD_GRID_TYPE]
  const handleAddDef = useCallback((def, position) => {
    if (def) addBlock(def, position)
  }, [addBlock])

  // Les CONTENANTS, regroupés. Grille et gabarits en colonnes répondent à une
  // seule intention — « découper la page » — et les lister à plat mettait
  // quatre entrées quasi jumelles en face des deux vrais gestes d'ajout. Ils
  // passent donc derrière une entrée unique, qui déplie ses options.
  const layoutItems = [
    ...(gridDef ? [{ key: 'grid', Icon: LayoutGrid, label: 'Grille', hint: 'Des cases à dessiner, chacune recevant un bloc', run: (position) => handleAddDef(gridDef, position) }] : []),
    ...LAYOUT_PRESETS.flatMap(({ type, Icon, label, hint }) => {
      const def = registryByType[type]
      return def ? [{ key: type, Icon, label, hint, run: (position) => handleAddDef(def, position) }] : []
    }),
  ]

  // Le menu du liseré d'insertion, en données : le composant ne connaît aucun
  // geste en particulier, il rend ce qu'on lui donne — une entrée porte soit un
  // `run`, soit des `children` (une sous-liste). Un geste d'ajout de plus
  // s'ajoute ici, pas dans la barre.
  const addItems = [
    { key: 'new', Icon: Plus, label: 'Ajouter un bloc', hint: 'Poser un bloc neuf depuis le catalogue', run: handleOpenCatalog },
    { key: 'existing', Icon: Link2, label: 'Bloc existant', hint: 'Réutiliser un bloc déjà posé — son contenu reste commun à toutes ses places', run: handleOpenExisting },
    // Aucun contenant dans le registre → pas d'entrée qui ouvrirait du vide.
    ...(layoutItems.length ? [{
      key: 'layout',
      Icon: LayoutTemplate,
      label: 'Mise en page',
      hint: 'Découper la page : une grille ou un gabarit en colonnes, à remplir ensuite',
      children: layoutItems,
    }] : []),
  ]

  if (loading) {
    return (
      <div key="builder-canvas-loading" id="builder-canvas" className="flex-1 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-teal-700" />
      </div>
    )
  }

  if (accessDenied) {
    return (
      <div key="builder-canvas-denied" id="builder-canvas" className="flex-1">
        <FeatureUnavailable
          title="Tableau de bord inaccessible"
          detail="Ce tableau de bord n'existe pas ou n'est pas partagé avec vous. Demandez l'accès à son propriétaire."
        />
      </div>
    )
  }

  if (hostUnavailable) {
    return (
      <div key="builder-canvas-unavailable" id="builder-canvas" className="flex-1">
        <FeatureUnavailable
          title="Tableau de bord indisponible"
          detail="La fonctionnalité Tableaux de bord n'est pas active sur ce serveur, ou ce tableau de bord n'existe pas."
        />
      </div>
    )
  }

  const isConstrained = previewDevice !== 'desktop'

  // Consultation d'une VERSION (cf. BuilderVersions) : le canevas rend ses blocs
  // à la place de ceux de la page, par le MÊME montage — l'édition est coupée
  // par le contexte, et un bloc de version n'a pas d'URL d'écriture. La page
  // éditée n'est pas touchée : sortir de la consultation la rend telle quelle.
  // En COMPARAISON, ce sont les entrées fusionnées des deux états, chacune dans
  // son cadre de différence.
  const viewing = versionView?.mode === 'view'
  const comparing = versionView?.mode === 'compare'
  const blocs = viewing ? versionView.page.blocs : page.blocs
  const renderCompareBloc = (shown, side) => {
    // L'avant et l'après d'un bloc modifié portent le MÊME builderId, et le
    // canevas relie les montages d'une même unité (registre, miroir) : montés
    // ensemble (« Les deux »), l'avant affichait le contenu de l'après. Le
    // côté « avant » prend donc une identité à lui.
    const bloc = side === 'before' ? { ...shown, id: `${shown.id}~avant` } : shown
    const def = registryByType[bloc.type]
    if (!def?.rjBuild) {
      return (
        <div className="p-4 text-xs text-red-500 bg-red-50 border border-red-200 rounded">
          Type de bloc inconnu : <code>{bloc.type}</code>
        </div>
      )
    }
    return (
      <BuilderBlockWrapper bloc={bloc} index={0} total={1}>
        <BuilderBlockMount key={`${def.version}-${side}`} bloc={bloc} def={def} objectContext={objectContext} alternates={alternates} apiPrefix={apiPrefix} />
      </BuilderBlockWrapper>
    )
  }

  return (
    <div key="builder-canvas-loaded" className="flex-1 w-full">
      {canEditStructure && <BuilderChatPanel host={host} onDashboardInvalidate={handleDashboardInvalidate} />}
      <div
        className={`transition-all duration-300 ease-in-out motion-reduce:transition-none ${
          isConstrained ? 'mx-auto border-x border-stone-200/60 shadow-lg bg-white' : ''
        }`}
        style={{ maxWidth: DEVICE_WIDTHS[previewDevice] }}
      >
        {/* La clé remonte TOUT le canevas quand ce qu'il montre change d'origine
            (page ↔ version ↔ comparaison, ou rechargement après restauration) :
            un bloc garde sinon, sous le même builderId, le store de l'état
            précédent (dataOverride mémoïsé). */}
        <main key={versionView?.key ?? `page-${reloadToken}`} id="builder-canvas" data-uno-root className="@container w-full">
          {comparing ? (
            <>
              <VersionCompareSummary />
              {versionView.entries.map(entry => (
                <VersionDiffFrame key={entry.key} entry={entry} renderBloc={renderCompareBloc} />
              ))}
            </>
          ) : (<>
          {/* Page vide en édition : la barre elle-même porte l'amorce (bouton
              centré, visible sans survol) — sinon le seul repère serait un
              liseré qui n'apparaît qu'au passage de la souris. */}
          <AddBlockButton
            position={0}
            isEditMode={canEditStructure}
            items={addItems}
            variant={blocs.length === 0 ? 'empty' : 'inline'}
          />

          {blocs.length === 0 && !isEditMode && (
            <div className="text-center py-24 text-gray-400 max-w-lg mx-auto px-4">
              <p className="text-sm">Cette page ne contient aucun bloc.</p>
            </div>
          )}

          {/* LayoutStrategy (lot L1): 2-D grid for dashboards, linear stack for
              pages. The grid mounts the SAME BuilderBlockMount so blocks stay
              editable; position lives per-bloc in `bloc.layout`. */}
          {layoutMode === 'grid' ? (
            <BuilderGridLayout
              blocs={blocs}
              registryByType={registryByType}
              objectContext={objectContext}
              isEditMode={canEditStructure}
            />
          ) : blocs.map((bloc, idx) => {
            const def = registryByType[bloc.type]
            // Wait for the registry before mounting the bloc. Without the def,
            // we have no rjBuild to render with.
            if (!def?.rjBuild) {
              return (
                <div key={bloc.id}>
                  {Array.from({ length: skeletonsAt(idx) }, (_, n) => (
                    <Fragment key={`sk-${n}`}>
                      <BuilderBlockSkeleton />
                      {/* Le carré porte son propre liseré : sans lui, la seule
                          façon d'enchaîner une demande était de viser AU-DESSUS
                          de celle qui travaille — le liseré suivant appartient
                          au bloc, qui n'est pas encore là. */}
                      <AddBlockButton position={idx} isEditMode={isEditMode} items={addItems} />
                    </Fragment>
                  ))}
                  <BuilderBlockWrapper bloc={bloc} index={idx} total={blocs.length}>
                    <div className="p-4 text-xs text-red-500 bg-red-50 border border-red-200 rounded">
                      Unknown block type: <code>{bloc.type}</code>
                    </div>
                  </BuilderBlockWrapper>
                </div>
              )
            }
            return (
              // In read-only render this per-bloc div is collapsed to
              // `display: contents` (via `.builder-rbox`) so the block's own
              // root becomes a direct child of the tall `<main>` — required for
              // a top-level block to use `position: sticky` (e.g. a sticky site
              // header). In edit mode it stays a real box so AddBlockButton can
              // position itself. In published mode it also carries the
              // `data-builder-hide` attribute, so the responsive-visibility
              // container queries (index.css) actually hide it at the right
              // viewport — `hiddenOn` is a no-op in the editor, where the muted
              // overlay in BuilderBlockWrapper previews the effect instead.
              <div
                key={bloc.id}
                className={canEditStructure ? undefined : RBOX_CLASS}
                {...(canEditStructure ? {} : { [HIDE_ATTR]: hideAttrValue(bloc.hiddenOn) })}
              >
                {/* La place réservée est rendue DANS la boîte du bloc qui la suit,
                    avant lui : en mode édition cette boîte est un vrai bloc de
                    flux, donc le carré apparaît exactement où le bloc ira. */}
                {Array.from({ length: skeletonsAt(idx) }, (_, n) => (
                    <Fragment key={`sk-${n}`}>
                      <BuilderBlockSkeleton />
                      {/* Le carré porte son propre liseré : sans lui, la seule
                          façon d'enchaîner une demande était de viser AU-DESSUS
                          de celle qui travaille — le liseré suivant appartient
                          au bloc, qui n'est pas encore là. */}
                      <AddBlockButton position={idx} isEditMode={isEditMode} items={addItems} />
                    </Fragment>
                  ))}
                <BuilderBlockWrapper bloc={bloc} index={idx} total={blocs.length}>
                  {/* `key` on the def version: when the assistant rewrites a
                      block type, the mounted instances remount on the new
                      definition instead of keeping the old render tree. */}
                  <BuilderBlockMount key={def.version} bloc={bloc} def={def} objectContext={objectContext} alternates={alternates} apiPrefix={apiPrefix} />
                </BuilderBlockWrapper>
                <AddBlockButton position={idx + 1} isEditMode={canEditStructure} items={addItems} />
              </div>
            )
          })}

          {/* Le « + » du bas de page : la place réservée n'a aucun bloc derrière
              lequel se ranger, elle vient donc après la liste. */}
          {Array.from({ length: skeletonsAt(blocs.length) }, (_, n) => (
            <Fragment key={`sk-end-${n}`}>
              <BuilderBlockSkeleton />
              <AddBlockButton position={blocs.length} isEditMode={isEditMode} items={addItems} />
            </Fragment>
          ))}

          </>)}

          {catalogPosition !== null && (
            catalogMode === 'existing' ? (
              <BuilderExistingBlockPicker
                position={catalogPosition}
                onClose={handleCloseCatalog}
              />
            ) : (
              <BuilderBlockCatalog
                position={catalogPosition}
                accept={catalogAccept}
                onClose={handleCloseCatalog}
              />
            )
          )}
        </main>
      </div>
    </div>
  )
}

/**
 * Le liseré d'insertion : UNE cible, un menu déroulant.
 *
 * Historique : les gestes d'ajout étaient rendus côte à côte, en contrôle
 * segmenté. À deux (« bloc », « grille ») ça tenait ; à six (les gabarits en
 * colonnes sont venus s'ajouter) la bande devenait une barre d'outils posée en
 * travers du canevas, illisible et plus large que bien des blocs. Un seul
 * déclencheur « Ajouter » ouvre donc la liste des gestes, chacun avec son
 * intitulé ET son explication — ce que le contrôle segmenté ne pouvait pas
 * porter faute de place.
 *
 * Les CONTENANTS (grille, gabarits en colonnes) sont eux-mêmes repliés derrière
 * « Mise en page » : à plat, quatre entrées jumelles écrasaient les deux vrais
 * gestes d'ajout. Le menu se déplie SUR PLACE (pas de second panneau flottant
 * à survoler) — une entrée « Retour » ramène à la première liste.
 *
 * Détails d'ergonomie à conserver :
 *  - `group-focus-within` : au clavier, le liseré apparaît quand on l'atteint.
 *    Sans ça on tabulait dans un bouton INVISIBLE (WCAG 2.4.7).
 *  - Le liseré reste visible tant que son menu est ouvert : le menu est rendu
 *    en PORTAIL (donc hors du sous-arbre survolé/focalisé), sans quoi la bande
 *    disparaîtrait sous le menu qu'elle vient d'ouvrir.
 *  - `aria-label` situé — « Ajouter en position 3 » : la page en compte autant
 *    que d'intervalles, et une liste de boutons homonymes est illisible en
 *    restitution vocale.
 *  - 28 px de haut, au-dessus du minimum de 24 px (WCAG 2.5.8).
 *
 * Le composant est scindé en deux : la garde `isEditMode` doit rester AVANT
 * tout état, or la barre en a désormais (menu ouvert). Un `return null` placé
 * avant des hooks changerait leur ordre d'un rendu à l'autre.
 */
const AddBlockButton = ({ isEditMode, ...rest }) => (isEditMode ? <AddBlockBar {...rest} /> : null)

const ADD_MENU_WIDTH = 320
const ADD_MENU_ITEM_HEIGHT = 52

const AddBlockBar = ({ position, items, variant = 'inline' }) => {
  const [open, setOpen] = useState(false)
  // Clé de l'entrée dépliée (« Mise en page »), ou null pour la liste racine.
  const [subKey, setSubKey] = useState(null)
  const [pos, setPos] = useState(null)
  const triggerRef = useRef(null)
  const menuRef = useRef(null)
  const menuId = useId()
  const sub = subKey ? items.find(i => i.key === subKey) : null
  const view = sub ? sub.children : items
  // Le menu se ferme sur la liste racine : rouvrir ne doit pas rendre la
  // sous-liste d'un geste précédent.
  useEffect(() => { if (!open) setSubKey(null) }, [open])
  // Page vide : il n'y a rien à survoler, donc rien ne révélerait le liseré.
  // Le même déclencheur devient alors une zone d'amorce, centrée et visible
  // d'emblée.
  const isEmpty = variant === 'empty'
  const where = isEmpty ? 'le premier bloc' : `en position ${position + 1}`

  // Le sens de bascule se décide sur la liste la PLUS LONGUE (sous-liste
  // dépliée comprise, + son « Retour ») : sinon déplier « Mise en page » d'un
  // menu ouvert vers le bas le ferait déborder sous la fenêtre.
  const menuRows = Math.max(items.length, ...items.map(i => (i.children?.length ?? 0) + 1))

  // Position ancrée au déclencheur, recalculée au défilement et au
  // redimensionnement : rendu en portail, le menu ne suit plus sa bande tout
  // seul. Il bascule vers le haut quand le liseré est bas dans la fenêtre, et
  // reste dans le viewport horizontalement.
  useEffect(() => {
    if (!open) return
    const place = () => {
      const rect = triggerRef.current?.getBoundingClientRect()
      if (!rect) return
      const wanted = rect.left + rect.width / 2 - ADD_MENU_WIDTH / 2
      const left = Math.min(Math.max(8, wanted), largeurUtile() - ADD_MENU_WIDTH - 8)
      setPos(
        window.innerHeight - rect.bottom < menuRows * ADD_MENU_ITEM_HEIGHT + 12
          ? { left, bottom: window.innerHeight - rect.top + 6 }
          : { left, top: rect.bottom + 6 }
      )
    }
    place()
    // Capture : le défilement du canevas ne bouillonne pas jusqu'à `window`,
    // or c'est LUI qui déplace le déclencheur.
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    return () => {
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
    }
  }, [open, menuRows])

  useEffect(() => {
    if (!open) return
    // Le menu vit hors du sous-arbre du déclencheur (portail) : les deux
    // conteneurs comptent comme « dedans », sinon il se fermerait sur ses
    // propres clics.
    const onDocClick = (e) => {
      if (triggerRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open])

  const close = ({ refocus = true } = {}) => {
    setOpen(false)
    if (refocus) triggerRef.current?.focus()
  }

  // Le focus suit la liste affichée, sinon il resterait sur un bouton démonté
  // et repartirait à la racine du document.
  const focusItem = (index) => requestAnimationFrame(() => {
    const buttons = menuRef.current?.querySelectorAll('[role="menuitem"]')
    buttons?.[index < 0 ? buttons.length + index : index]?.focus()
  })

  // Déplier : la sous-liste remplace la liste, focus sur sa première option
  // (« Retour » est en tête, on entre donc sur l'option suivante).
  const openSub = (key) => { setSubKey(key); focusItem(1) }
  // Replier : retour à la racine, focus rendu à l'entrée d'où l'on vient.
  const closeSub = () => {
    const at = items.findIndex(i => i.key === subKey)
    setSubKey(null)
    focusItem(Math.max(at, 0))
  }

  // Échap replie la sous-liste si l'on y est, sinon referme le menu et rend le
  // focus au déclencheur — sinon il repart à la racine du document et l'on
  // retraverse la page entière au clavier.
  useEscapeClose(() => (sub ? closeSub() : close()), open)

  // Le menu est une liste de commandes : les flèches y naviguent (WAI-ARIA
  // « menu button »), Tab en sort en le refermant. Droite/gauche entrent dans
  // une sous-liste et en ressortent, comme dans un menu système.
  const onMenuKeyDown = (e) => {
    const buttons = Array.from(menuRef.current?.querySelectorAll('[role="menuitem"]') ?? [])
    const at = buttons.indexOf(document.activeElement)
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      const next = e.key === 'ArrowDown' ? at + 1 : at - 1
      buttons[(next + buttons.length) % buttons.length]?.focus()
    } else if (e.key === 'Home') { e.preventDefault(); buttons[0]?.focus() }
    else if (e.key === 'End') { e.preventDefault(); buttons[buttons.length - 1]?.focus() }
    else if (e.key === 'ArrowRight' && !sub) {
      const item = view[at]
      if (item?.children) { e.preventDefault(); openSub(item.key) }
    } else if (e.key === 'ArrowLeft' && sub) { e.preventDefault(); closeSub() }
    else if (e.key === 'Tab') { close() }
  }

  const trigger = (
    <button
      ref={triggerRef}
      type="button"
      onClick={() => setOpen(o => !o)}
      onKeyDown={(e) => {
        // Flèche bas depuis le déclencheur : ouvre ET entre dans le menu.
        if (e.key === 'ArrowDown') {
          e.preventDefault()
          setOpen(true)
          requestAnimationFrame(() => menuRef.current?.querySelector('[role="menuitem"]')?.focus())
        }
      }}
      aria-haspopup="menu"
      aria-expanded={open}
      aria-controls={open ? menuId : undefined}
      aria-label={`Ajouter ${where}`}
      title="Ajouter ici : un bloc, un bloc existant ou une mise en page"
      className={`inline-flex flex-shrink-0 items-center gap-1.5 rounded-full bg-teal-600 font-semibold text-white shadow-md ring-1 ring-teal-500/40 transition-colors hover:bg-teal-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-1 ${
        isEmpty ? 'h-10 px-5 text-sm' : 'h-7 px-3 text-xs'
      }`}
    >
      <Plus size={isEmpty ? 16 : 13} className="flex-shrink-0" aria-hidden="true" />
      {isEmpty ? 'Ajouter un bloc' : 'Ajouter'}
      <ChevronDown size={isEmpty ? 15 : 13} className="flex-shrink-0 opacity-80" aria-hidden="true" />
    </button>
  )

  // La bande chevauche la frontière entre deux blocs (`top: -16`), sans rien
  // occuper. La PREMIÈRE n'a pas de bloc au-dessus d'elle : ces 16 px la
  // faisaient mordre sur la barre d'outils du builder, `sticky` en z-30 — la
  // pastille y passait dessous et n'en montrait qu'une moitié, en permanence.
  // Elle prend donc sa propre hauteur, entièrement sous la barre.
  const isFirst = position === 0

  const band = (
    <div className="relative" style={{ height: isFirst ? 32 : 0, zIndex: 10 }}>
      {/* La bande sensible court sur toute la largeur : viser le liseré ne
          demande pas de viser le bouton. */}
      <div
        className="group absolute left-0 right-0 flex items-center"
        style={{ top: isFirst ? 0 : -16, height: 32 }}
      >
        <div
          className={`flex w-full items-center gap-2 px-6 transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100 motion-reduce:transition-none ${open ? 'opacity-100' : 'opacity-0'}`}
        >
          <span className="h-px flex-1 bg-teal-500/60" aria-hidden="true" />
          {trigger}
          <span className="h-px flex-1 bg-teal-500/60" aria-hidden="true" />
        </div>
      </div>
    </div>
  )

  return (
    <>
      {isEmpty ? (
        <div className="px-6 py-20">
          <div className="mx-auto flex max-w-lg flex-col items-center gap-4 rounded-xl border-2 border-dashed border-teal-300 bg-teal-50/40 px-6 py-12 text-center">
            <p className="text-sm text-slate-600">Cette page ne contient aucun bloc.</p>
            {trigger}
            <p className="text-xs text-slate-500">
              Un bloc neuf, un bloc déjà posé, ou une mise en page à remplir.
            </p>
          </div>
        </div>
      ) : band}

      {open && pos && createPortal(
        <div
          id={menuId}
          ref={menuRef}
          role="menu"
          aria-label={sub ? `${sub.label} — ajouter ${where}` : `Ajouter ${where}`}
          onKeyDown={onMenuKeyDown}
          className="popover-menu"
          style={{ ...pos, width: ADD_MENU_WIDTH }}
        >
          {/* Sous-liste : son titre EST le chemin du retour. Un intitulé seul
              laisserait le clavier sans issue autre qu'Échap. */}
          {sub && (
            <button
              type="button"
              role="menuitem"
              onClick={closeSub}
              className="flex w-full items-center gap-2 border-b border-slate-100 px-3 py-2 text-left text-xs font-semibold text-slate-500 hover:bg-teal-50 focus-visible:bg-teal-50 focus-visible:outline-none"
            >
              <ChevronLeft size={14} className="shrink-0" aria-hidden="true" />
              {sub.label}
            </button>
          )}
          {view.map((item, index) => (
            <button
              key={item.key}
              type="button"
              role="menuitem"
              // Une entrée ne fait qu'une chose : dérouler ses options, ou
              // poser le bloc et refermer.
              {...(item.children ? { 'aria-haspopup': 'true', 'aria-expanded': false } : {})}
              onClick={() => {
                if (item.children) { openSub(item.key); return }
                close({ refocus: false })
                item.run(position)
              }}
              className={`flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-teal-50 focus-visible:bg-teal-50 focus-visible:outline-none ${index > 0 ? 'border-t border-slate-100' : ''}`}
            >
              <item.Icon size={14} className="mt-0.5 shrink-0 text-slate-400" aria-hidden="true" />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-slate-800">{item.label}</span>
                <span className="block text-xs text-slate-500">{item.hint}</span>
              </span>
              {item.children && <ChevronRight size={14} className="mt-0.5 shrink-0 text-slate-400" aria-hidden="true" />}
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  )
}
